import type { User, Workspace } from '@/types/auth';
import type {
  CurrentUserResponse,
  GetUserContextResponse,
} from '@/types/api/responses';
import { apiClient } from '@/services/api';

// ===== STATE INTERFACE =====

export interface AuthState {
  user: User | null;
  workspaces: Workspace[];
  currentWorkspaceId: string | null;
  isAuthenticated: boolean;
  isCheckingAuth: boolean;
  sessionExpired: boolean;
}

// ===== TYPES =====

export interface UserPreferencesUpdate {
  preferredLanguage?: string;
  preferredTheme?: string;
}

export interface UserProfileUpdate {
  displayName?: string;
  avatarUrl?: string | null;
}

// ===== ACTIONS INTERFACE =====

export interface AuthActions {
  checkAuth: () => Promise<void>;
  applyBootstrapContext: (context: GetUserContextResponse) => void;
  logout: () => void;
  setCurrentWorkspace: (workspaceId: string) => void;
  setSessionExpired: () => void;
  updateUserPreferences: (prefs: UserPreferencesUpdate) => void;
  updateUserProfile: (update: UserProfileUpdate) => void;
  updateUserEmail: (email: string) => void;
  patchAuthWorkspace: (
    workspaceId: string,
    patch: { name: string; description: string | null }
  ) => void;
  setWorkspaceMapConfigured: (workspaceId: string, configured: boolean) => void;
}

// ===== SLICE INTERFACE =====

export interface AuthSlice {
  auth: AuthState & AuthActions;
}

// ===== HELPERS =====

/**
 * Resolves which workspace should be active after a (re)hydration.
 * Keeps the current selection when it's still a valid member workspace;
 * otherwise prefers the last-used workspace, then the first available one.
 */
function resolveCurrentWorkspaceId(
  existingId: string | null,
  workspaceIds: string[],
  lastWorkspaceId: string | null | undefined
): string | null {
  if (existingId && workspaceIds.includes(existingId)) {
    return existingId;
  }
  if (lastWorkspaceId && workspaceIds.includes(lastWorkspaceId)) {
    return lastWorkspaceId;
  }
  return workspaceIds[0] ?? null;
}

// ===== SLICE CREATOR =====

export const createAuthSlice = (
  set: (fn: (state: AuthSlice) => void) => void,
  get: () => AuthSlice,
  _store: unknown
) => ({
  auth: {
    // Initial state
    user: null,
    workspaces: [],
    currentWorkspaceId: null,
    isAuthenticated: false,
    isCheckingAuth: false,
    sessionExpired: false,

    // Actions
    checkAuth: async () => {
      // Prevent multiple concurrent auth checks (React StrictMode calls effects twice)
      if (get().auth.isCheckingAuth) {
        return;
      }

      set((state) => {
        state.auth.isCheckingAuth = true;
      });

      try {
        // Use apiClient instead of fetch - handles credentials and token refresh automatically
        const response = await apiClient.get<CurrentUserResponse>('/auth/me');
        const data = response.data;

        set((state) => {
          state.auth.user = {
            id: data.userId,
            email: data.email,
            displayName: data.displayName,
            systemRole: data.systemRole ?? null,
            subscriptionTier: data.subscriptionTier ?? 'Free',
            subscriptionValidUntil: data.subscriptionValidUntil ?? null,
            avatarUrl: data.avatarUrl ?? null,
            preferredLanguage: data.preferredLanguage ?? null,
            preferredTheme: data.preferredTheme ?? null,
          };
          state.auth.workspaces = (data.workspaces ?? []).map((workspace) => ({
            id: workspace.id,
            shortId: workspace.shortId,
            name: workspace.name,
            description: workspace.description ?? null,
            role: workspace.role,
            isOwner: workspace.isOwner,
            isMapConfigured: workspace.isMapConfigured ?? false,
          }));
          state.auth.currentWorkspaceId = resolveCurrentWorkspaceId(
            state.auth.currentWorkspaceId,
            (data.workspaces ?? []).map((workspace) => workspace.id),
            data.lastWorkspaceId
          );
          state.auth.isAuthenticated = true;
          state.auth.isCheckingAuth = false;
          state.auth.sessionExpired = false;
        });
      } catch {
        set((state) => {
          state.auth.user = null;
          state.auth.workspaces = [];
          state.auth.currentWorkspaceId = null;
          state.auth.isAuthenticated = false;
          state.auth.isCheckingAuth = false;
        });
      }
    },

    applyBootstrapContext: (context: GetUserContextResponse) => {
      set((state) => {
        state.auth.user = {
          id: context.userId,
          email: context.email,
          displayName: context.displayName,
          systemRole: context.systemRole ?? null,
          // Subscription data is not part of the context payload; default it
          // the same way checkAuth does until a dedicated source exists.
          subscriptionTier: 'Free',
          subscriptionValidUntil: null,
          avatarUrl: context.avatarUrl ?? null,
          preferredLanguage: context.preferredLanguage ?? null,
          preferredTheme: context.preferredTheme ?? null,
        };
        // Lightweight projection used by the auth-level workspace list; the
        // full WorkspaceListItem (incl. mapSettings) lives in the workspace slice.
        state.auth.workspaces = context.workspaces.map((workspace) => ({
          id: workspace.workspaceId,
          shortId: workspace.shortId,
          name: workspace.name,
          description: workspace.description ?? null,
          role: workspace.role,
          isOwner: workspace.isOwner,
          isMapConfigured: workspace.isMapConfigured,
        }));

        state.auth.currentWorkspaceId = resolveCurrentWorkspaceId(
          state.auth.currentWorkspaceId,
          context.workspaces.map((workspace) => workspace.workspaceId),
          context.lastWorkspaceId
        );

        state.auth.isAuthenticated = true;
        state.auth.isCheckingAuth = false;
        state.auth.sessionExpired = false;
      });
    },

    logout: () => {
      // Navigate IMMEDIATELY - don't clear state first!
      // Clearing state triggers re-render → auth redirect → cancels logout navigation
      // State will be cleared anyway when page reloads after KeyCloak logout
      window.location.href = '/auth/logout';
    },

    setCurrentWorkspace: (workspaceId: string) => {
      set((state) => {
        state.auth.currentWorkspaceId = workspaceId;
      });
    },

    setSessionExpired: () => {
      if (get().auth.isAuthenticated) {
        set((state) => {
          state.auth.sessionExpired = true;
        });
      }
    },

    updateUserPreferences: (prefs: UserPreferencesUpdate) => {
      set((state) => {
        if (state.auth.user) {
          if (prefs.preferredLanguage !== undefined) {
            state.auth.user.preferredLanguage = prefs.preferredLanguage;
          }
          if (prefs.preferredTheme !== undefined) {
            state.auth.user.preferredTheme = prefs.preferredTheme;
          }
        }
      });
    },

    updateUserProfile: (update: UserProfileUpdate) => {
      set((state) => {
        if (state.auth.user) {
          if (update.displayName !== undefined) {
            state.auth.user.displayName = update.displayName;
          }
          if (update.avatarUrl !== undefined) {
            state.auth.user.avatarUrl = update.avatarUrl;
          }
        }
      });
    },

    updateUserEmail: (email: string) => {
      set((state) => {
        if (state.auth.user) {
          state.auth.user.email = email;
        }
      });
    },

    patchAuthWorkspace: (
      workspaceId: string,
      patch: { name: string; description: string | null }
    ) => {
      set((state) => {
        const workspace = state.auth.workspaces.find(
          (ws) => ws.id === workspaceId
        );
        if (workspace) {
          workspace.name = patch.name;
          workspace.description = patch.description;
        }
      });
    },

    setWorkspaceMapConfigured: (workspaceId: string, configured: boolean) => {
      set((state) => {
        const workspace = state.auth.workspaces.find(
          (ws) => ws.id === workspaceId
        );
        if (workspace) {
          workspace.isMapConfigured = configured;
        }
      });
    },
  },
});
